import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { ChampionshipApiService } from '../../services/championship-api/ChampionshipApiService';
import { EventApiService } from '../../services/event-api/EventApiService';
import { SeasonApiService } from '../../services/season-api/SeasonApiService';
import {
  Championship,
  EventSeasonType,
  SaveSeasonData,
  SaveSeasonPhaseData,
  Season,
  SeasonPhase,
  SportsEvent,
} from '../../types/Event';

interface SeasonStat {
  eventsTotal: number;
  ticketsTotal: number;
  soldTicketsTotal: number;
  purchaseTotal: number;
  plannedRevenue: number;
  paidRevenue: number;
  expenseTotal: number;
  plannedProfit: number;
  actualProfit: number;
}

interface PhaseTemplate {
  name: string;
  slug: string;
  type: EventSeasonType;
  sortOrder: number;
  parentSlug?: string;
}

@Component({
  selector: 'app-season-directory-page',
  standalone: false,
  templateUrl: './season-directory-page.component.html',
  styleUrl: './season-directory-page.component.sass',
})
export class SeasonDirectoryPageComponent implements OnInit {
  public seasons: Season[] = [];
  public championships: Championship[] = [];
  public events: SportsEvent[] = [];
  public formData: SaveSeasonData = this.createForm();
  public phaseFormData: SaveSeasonPhaseData = this.createPhaseForm();
  public selectedSeasonId = '';
  public editingSeasonId: string | null = null;
  public editingPhaseId: string | null = null;
  public modalOpen = false;
  public phaseModalOpen = false;
  public loading = false;
  public saving = false;
  public syncingPhases = false;
  public errorMessage: string | null = null;
  public successMessage: string | null = null;

  public constructor(
    private seasonApi: SeasonApiService,
    private championshipApi: ChampionshipApiService,
    private eventApi: EventApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    this.loading = true;
    try {
      await Promise.all([
        this.loadChampionships(),
        this.loadSeasons(),
        this.loadEvents(),
      ]);
      this.selectedSeasonId = this.sortedSeasons.find((season) => season.isCurrent)?.id ?? this.sortedSeasons[0]?.id ?? '';
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  public get sortedSeasons(): Season[] {
    return [...this.seasons].sort((left, right) => (
      Number(right.isCurrent) - Number(left.isCurrent)
      || this.getChampionshipName(left).localeCompare(this.getChampionshipName(right), 'ru')
      || right.startYear - left.startYear
    ));
  }

  public get selectedSeason(): Season | null {
    return this.seasons.find((season) => season.id === this.selectedSeasonId) ?? null;
  }

  public get selectedChampionship(): Championship | null {
    const season = this.selectedSeason;
    return season ? this.championships.find((championship) => championship.id === season.championshipId) ?? null : null;
  }

  public get selectedSeasonEvents(): SportsEvent[] {
    const season = this.selectedSeason;
    return season ? this.events.filter((event) => event.seasonId === season.id) : [];
  }

  public get flattenedSelectedPhases(): SeasonPhase[] {
    return this.selectedSeason ? this.flattenPhases(this.selectedSeason.phases) : [];
  }

  public get rootSelectedPhases(): SeasonPhase[] {
    return this.selectedSeason?.phases ?? [];
  }

  public get totalStats(): SeasonStat {
    return this.createSeasonStat(this.selectedSeasonEvents);
  }

  public get regularStats(): SeasonStat {
    return this.createSeasonStat(this.selectedSeasonEvents.filter((event) => event.seasonType === 'regular'));
  }

  public get playoffStats(): SeasonStat {
    return this.createSeasonStat(this.selectedSeasonEvents.filter((event) => event.seasonType === 'playoff'));
  }

  public getSeasonStats(season: Season): SeasonStat {
    return this.createSeasonStat(this.events.filter((event) => event.seasonId === season.id));
  }

  public get phaseTypeOptions(): Array<{ value: EventSeasonType; label: string }> {
    if (this.isRplChampionship(this.selectedChampionship)) {
      return [{ value: 'regular', label: 'Регулярный сезон' }];
    }
    if (this.isKhlChampionship(this.selectedChampionship)) {
      return [
        { value: 'regular', label: 'Регулярный сезон' },
        { value: 'playoff', label: 'Плей-офф' },
        { value: 'playoff_stage', label: 'Стадия плей-офф' },
      ];
    }

    return [
      { value: 'regular', label: 'Регулярный сезон' },
      { value: 'playoff', label: 'Плей-офф' },
      { value: 'playoff_stage', label: 'Стадия плей-офф' },
      { value: 'cup', label: 'Кубок' },
      { value: 'special', label: 'Спец. этап' },
    ];
  }

  public get phaseParentOptions(): SeasonPhase[] {
    return this.flattenedSelectedPhases.filter((phase) => phase.type === 'playoff' && phase.id !== this.editingPhaseId);
  }

  public get canSavePhase(): boolean {
    return Boolean(this.selectedSeason)
      && Boolean(this.phaseFormData.name.trim())
      && this.phaseFormData.sortOrder > 0
      && (this.phaseFormData.type !== 'playoff_stage' || Boolean(this.phaseFormData.parentPhaseId));
  }

  public selectSeason(season: Season): void {
    this.selectedSeasonId = season.id;
  }

  public openCreate(): void {
    this.editingSeasonId = null;
    this.formData = this.createForm();
    this.modalOpen = true;
  }

  public openEdit(season: Season): void {
    this.editingSeasonId = season.id;
    this.formData = {
      championshipId: season.championshipId,
      seasonYear: season.seasonYear,
      startsOn: null,
      endsOn: null,
      isCurrent: season.isCurrent,
    };
    this.modalOpen = true;
  }

  public closeModal(): void {
    if (!this.saving) {
      this.modalOpen = false;
    }
  }

  public async saveSeason(): Promise<void> {
    if (!this.formData.championshipId || !this.formData.seasonYear.trim()) {
      this.errorMessage = 'Заполните чемпионат и период сезона.';
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    try {
      const payload: SaveSeasonData = {
        championshipId: this.formData.championshipId,
        seasonYear: this.formData.seasonYear.trim(),
        startsOn: null,
        endsOn: null,
        isCurrent: Boolean(this.formData.isCurrent),
      };
      const response = this.editingSeasonId
        ? await this.seasonApi.updateSeason(this.editingSeasonId, payload)
        : await this.seasonApi.createSeason(payload);
      const season = this.unwrapResponse(response);
      if (!season) {
        return;
      }

      this.notificationService.notify('directory', this.editingSeasonId ? 'Сезон обновлен' : 'Сезон добавлен', season.name);
      this.successMessage = this.editingSeasonId ? 'Сезон обновлен.' : 'Сезон добавлен.';
      this.modalOpen = false;
      await this.loadSeasons();
      this.selectedSeasonId = season.id;
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public async deleteSeason(season: Season): Promise<void> {
    if (!window.confirm('Удалить сезон "' + season.name + ' ' + season.seasonYear + '"?')) {
      return;
    }

    try {
      const response = await this.seasonApi.deleteSeason(season.id);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }

      this.notificationService.notify('directory', 'Сезон удален', season.name);
      this.successMessage = 'Сезон удален.';
      await this.loadSeasons();
      this.selectedSeasonId = this.sortedSeasons[0]?.id ?? '';
    } catch (error) {
      this.handleUnknownError(error);
    }
  }

  public openCreatePhase(parentPhase: SeasonPhase | null = null): void {
    const selectedSeason = this.selectedSeason;
    if (!selectedSeason) {
      return;
    }
    this.editingPhaseId = null;
    this.phaseFormData = this.createPhaseForm({
      seasonId: selectedSeason.id,
      parentPhaseId: parentPhase?.id ?? null,
      type: parentPhase?.type === 'playoff' ? 'playoff_stage' : this.phaseTypeOptions[0]?.value ?? 'regular',
      sortOrder: this.getNextPhaseSortOrder(parentPhase?.id ?? null),
    });
    this.phaseModalOpen = true;
  }

  public openEditPhase(phase: SeasonPhase): void {
    this.editingPhaseId = phase.id;
    this.phaseFormData = {
      seasonId: phase.seasonId,
      parentPhaseId: phase.parentPhaseId,
      name: phase.name,
      slug: phase.slug,
      type: phase.type,
      sortOrder: phase.sortOrder,
      startsOn: null,
      endsOn: null,
    };
    this.phaseModalOpen = true;
  }

  public closePhaseModal(): void {
    if (!this.saving) {
      this.phaseModalOpen = false;
    }
  }

  public onPhaseTypeChange(): void {
    if (this.phaseFormData.type !== 'playoff_stage') {
      this.phaseFormData.parentPhaseId = null;
      return;
    }

    this.phaseFormData.parentPhaseId = this.phaseFormData.parentPhaseId || this.phaseParentOptions[0]?.id || null;
  }

  public async savePhase(): Promise<void> {
    const selectedSeason = this.selectedSeason;
    if (!selectedSeason || !this.canSavePhase) {
      this.errorMessage = 'Заполните название, тип и порядок фазы.';
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    try {
      const payload: SaveSeasonPhaseData = {
        seasonId: selectedSeason.id,
        parentPhaseId: this.phaseFormData.type === 'playoff_stage' ? this.phaseFormData.parentPhaseId : null,
        name: this.phaseFormData.name.trim(),
        slug: this.phaseFormData.slug?.trim() || null,
        type: this.phaseFormData.type,
        sortOrder: Number(this.phaseFormData.sortOrder),
        startsOn: null,
        endsOn: null,
      };
      const response = this.editingPhaseId
        ? await this.seasonApi.updateSeasonPhase(selectedSeason.id, this.editingPhaseId, payload)
        : await this.seasonApi.createSeasonPhase(selectedSeason.id, payload);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }

      this.notificationService.notify('directory', this.editingPhaseId ? 'Фаза обновлена' : 'Фаза добавлена', payload.name);
      this.successMessage = this.editingPhaseId ? 'Фаза обновлена.' : 'Фаза добавлена.';
      this.phaseModalOpen = false;
      await this.loadSeasons();
      this.selectedSeasonId = selectedSeason.id;
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public async deletePhase(phase: SeasonPhase): Promise<void> {
    const selectedSeason = this.selectedSeason;
    if (!selectedSeason || !window.confirm('Удалить фазу "' + phase.name + '"?')) {
      return;
    }

    try {
      const response = await this.seasonApi.deleteSeasonPhase(selectedSeason.id, phase.id);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }
      this.notificationService.notify('directory', 'Фаза удалена', phase.name);
      this.successMessage = 'Фаза удалена.';
      await this.loadSeasons();
      this.selectedSeasonId = selectedSeason.id;
    } catch (error) {
      this.handleUnknownError(error);
    }
  }

  public async syncDefaultPhases(): Promise<void> {
    const selectedSeason = this.selectedSeason;
    if (!selectedSeason) {
      return;
    }

    this.syncingPhases = true;
    this.errorMessage = null;
    try {
      const templates = this.getDefaultPhaseTemplates();
      const phaseBySlug = new Map(this.flattenedSelectedPhases.map((phase) => [phase.slug, phase]));
      for (const template of templates) {
        if (phaseBySlug.has(template.slug)) {
          continue;
        }
        const parentPhaseId = template.parentSlug ? phaseBySlug.get(template.parentSlug)?.id ?? null : null;
        if (template.parentSlug && !parentPhaseId) {
          continue;
        }
        const response = await this.seasonApi.createSeasonPhase(selectedSeason.id, {
          seasonId: selectedSeason.id,
          parentPhaseId,
          name: template.name,
          slug: template.slug,
          type: template.type,
          sortOrder: template.sortOrder,
          startsOn: null,
          endsOn: null,
        });
        const phase = this.unwrapResponse(response);
        if (phase) {
          phaseBySlug.set(phase.slug, phase);
        }
      }
      this.successMessage = 'Стандартные фазы проверены.';
      await this.loadSeasons();
      this.selectedSeasonId = selectedSeason.id;
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.syncingPhases = false;
      this.changeDetector.detectChanges();
    }
  }

  public getChampionshipName(season: Season): string {
    return this.championships.find((championship) => championship.id === season.championshipId)?.name ?? 'Не указан';
  }

  public getPhaseTypeName(type: EventSeasonType): string {
    const names: Record<EventSeasonType, string> = {
      cup: 'Кубок',
      playoff: 'Плей-офф',
      playoff_stage: 'Стадия',
      regular: 'Регулярный',
      special: 'Спец. этап',
    };
    return names[type];
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(value);
  }

  public formatStatLabel(stat: SeasonStat): string {
    return `${stat.eventsTotal} событий · ${stat.soldTicketsTotal}/${stat.ticketsTotal} билетов`;
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  private async loadChampionships(): Promise<void> {
    try {
      const response = await this.championshipApi.getChampionships();
      this.championships = this.unwrapResponse(response) ?? [];
      if (!this.formData.championshipId) {
        this.formData.championshipId = this.championships[0]?.id ?? '';
      }
    } catch (error) {
      this.handleUnknownError(error);
    }
  }

  private async loadSeasons(): Promise<void> {
    try {
      const response = await this.seasonApi.getSeasons();
      this.seasons = this.unwrapResponse(response) ?? [];
    } catch (error) {
      this.handleUnknownError(error);
    }
  }

  private async loadEvents(): Promise<void> {
    try {
      const response = await this.eventApi.getEvents();
      this.events = this.unwrapResponse(response) ?? [];
    } catch (error) {
      this.handleUnknownError(error);
    }
  }

  private createForm(): SaveSeasonData {
    return {
      championshipId: this.championships[0]?.id ?? '',
      seasonYear: this.getNextSeasonYear(),
      startsOn: null,
      endsOn: null,
      isCurrent: false,
    };
  }

  private createPhaseForm(overrides: Partial<SaveSeasonPhaseData> = {}): SaveSeasonPhaseData {
    return {
      seasonId: this.selectedSeasonId,
      parentPhaseId: null,
      name: '',
      slug: null,
      type: 'regular',
      sortOrder: 1,
      startsOn: null,
      endsOn: null,
      ...overrides,
    };
  }

  private createSeasonStat(events: SportsEvent[]): SeasonStat {
    const tickets = events.flatMap((event) => event.tickets);
    const soldTickets = tickets.filter((ticket) => ticket.status === 'sold');
    const soldPurchase = soldTickets.reduce((total, ticket) => total + ticket.purchasePrice, 0);
    const paidRevenue = soldTickets.reduce((total, ticket) => total + ticket.salePrice, 0);
    const expenseTotal = events.reduce((total, event) => total + event.summary.expenseTotal, 0);

    return {
      eventsTotal: events.length,
      ticketsTotal: tickets.length,
      soldTicketsTotal: soldTickets.length,
      purchaseTotal: tickets.reduce((total, ticket) => total + ticket.purchasePrice, 0),
      plannedRevenue: tickets.reduce((total, ticket) => total + ticket.salePrice, 0),
      paidRevenue,
      expenseTotal,
      plannedProfit: events.reduce((total, event) => total + event.summary.plannedProfitAfterExpenses, 0),
      actualProfit: paidRevenue - soldPurchase - expenseTotal,
    };
  }

  private flattenPhases(phases: SeasonPhase[]): SeasonPhase[] {
    return phases.flatMap((phase) => [phase, ...this.flattenPhases(phase.children)]);
  }

  private getNextPhaseSortOrder(parentPhaseId: string | null): number {
    const phases = parentPhaseId
      ? this.flattenedSelectedPhases.filter((phase) => phase.parentPhaseId === parentPhaseId)
      : this.rootSelectedPhases;
    return Math.max(0, ...phases.map((phase) => phase.sortOrder)) + 1;
  }

  private getDefaultPhaseTemplates(): PhaseTemplate[] {
    if (this.isKhlChampionship(this.selectedChampionship)) {
      return [
        {
          name: 'Регулярный сезон', slug: 'regular', type: 'regular', sortOrder: 1,
        },
        {
          name: 'Плей-офф', slug: 'playoff', type: 'playoff', sortOrder: 2,
        },
        {
          name: '1/8 финала', slug: 'playoff-1-8', type: 'playoff_stage', sortOrder: 1, parentSlug: 'playoff',
        },
        {
          name: '1/4 финала', slug: 'playoff-1-4', type: 'playoff_stage', sortOrder: 2, parentSlug: 'playoff',
        },
        {
          name: '1/2 финала', slug: 'playoff-1-2', type: 'playoff_stage', sortOrder: 3, parentSlug: 'playoff',
        },
        {
          name: 'Финал', slug: 'playoff-final', type: 'playoff_stage', sortOrder: 4, parentSlug: 'playoff',
        },
      ];
    }

    return [{
      name: 'Регулярный сезон', slug: 'regular', type: 'regular', sortOrder: 1,
    }];
  }

  private getNextSeasonYear(): string {
    const currentYear = new Date().getFullYear();
    return `${currentYear}/${currentYear + 1}`;
  }

  private isKhlChampionship(championship: Championship | null): boolean {
    const normalized = this.normalizeChampionshipName(championship);
    return normalized.includes('khl') || normalized.includes('кхл');
  }

  private isRplChampionship(championship: Championship | null): boolean {
    const normalized = this.normalizeChampionshipName(championship);
    return normalized.includes('rpl') || normalized.includes('рпл');
  }

  private normalizeChampionshipName(championship: Championship | null): string {
    return `${championship?.slug ?? ''} ${championship?.name ?? ''}`.toLowerCase();
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }
}
